import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Linkedin, Building } from 'lucide-react';
import { useBrands, Brand } from '../../hooks/useBrands';

interface SocialAccount {
  accountId: string;
  platform: string;
  pageId: string;
  username?: string;
}

interface LinkedInPostSettingsCardProps {
  organizationId: string;
  visibility: string;
  selectedBrandId: string;
  onOrganizationIdChange: (organizationId: string) => void;
  onVisibilityChange: (visibility: string) => void;
}

export const LinkedInPostSettingsCard: React.FC<LinkedInPostSettingsCardProps> = ({
  organizationId,
  visibility,
  selectedBrandId,
  onOrganizationIdChange,
  onVisibilityChange
}) => {
  const { t } = useTranslation();
  const { data: brandsData } = useBrands();
  
  // Find the selected brand
  const selectedBrand = brandsData?.brands?.find((brand: Brand) => brand.id === selectedBrandId);
  
  // Get LinkedIn accounts for the selected brand
  const linkedInAccounts = selectedBrand?.social_accounts?.filter(
    (account: SocialAccount) => account.platform.toLowerCase() === 'linkedin'
  ) || [];

  // Auto-select the first LinkedIn account if none is selected
  useEffect(() => {
    if (linkedInAccounts.length > 0 && !organizationId) {
      onOrganizationIdChange(linkedInAccounts[0].pageId);
    }
  }, [linkedInAccounts, organizationId, onOrganizationIdChange]);

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-6">
        <Linkedin className="w-5 h-5 text-[#0A66C2]" />
        <h2 className="text-lg font-semibold text-gray-900">LinkedIn Settings</h2>
      </div>

      <div className="space-y-4">
        {/* LinkedIn Company Page Selection */}
        {linkedInAccounts.length > 0 && (
          <div>
            <label htmlFor="linkedinAccount" className="block text-sm font-medium text-gray-700 mb-1">
              LinkedIn Company Page
            </label>
            <select
              id="linkedinAccount"
              value={organizationId}
              onChange={(e) => onOrganizationIdChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {linkedInAccounts.map((account: SocialAccount) => (
                <option key={account.accountId} value={account.pageId}>
                  {account.username || `Company Page (${account.pageId})`}
                </option>
              ))}
            </select>
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
              <Building className="w-3 h-3" />
              <span>Publishing as company page</span>
            </div>
          </div>
        )}

        {linkedInAccounts.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              No LinkedIn company pages are connected to this brand. Please connect a LinkedIn account in the Accounts section.
            </p>
          </div>
        )}

        {/* Visibility */}
        <div>
          <label htmlFor="visibility" className="block text-sm font-medium text-gray-700 mb-1">
            Post Visibility
          </label>
          <select
            id="visibility"
            value={visibility}
            onChange={(e) => onVisibilityChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="PUBLIC">Public</option>
            <option value="CONNECTIONS">Connections only</option>
          </select>
        </div>
      </div>
    </div>
  );
}; 